import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormKeys, WindowPercentageArea, WindowType } from '@nx-customer-apps/shared/enums';
import { FormOptions, RadioQuestionOverride } from '@nx-customer-apps/shared/interfaces';
import { RadioQuestion, windowTypeToOrder, windowTypeToPhrase } from '@nx-customer-apps/shared/utils';

@Injectable({
    providedIn: 'root'
})
export class WindowsAndGlazingFormService {
    public getWindowsAndGlazingQuestions(options: FormOptions): [RadioQuestion, RadioQuestion] {
        const typeOfGlazingOveride = options?.questions?.[FormKeys.TypeOfGlazing] as RadioQuestionOverride;
        if (!typeOfGlazingOveride?.options) {
            throw Error('Please specify options for "type of glazing" question');
        }
        const glazingType = this.getGlazingType(typeOfGlazingOveride);

        const windowsAreaOveride = options?.questions?.[FormKeys.WindowsPercentageArea] as RadioQuestionOverride;
        const windowsArea = this.getWindowsArea(windowsAreaOveride);
        return [windowsArea, glazingType];
    }

    private getWindowsArea(override?: RadioQuestionOverride): RadioQuestion {
        return new RadioQuestion({
            question: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.TITLE',
            description: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.DESCRIPTION',
            key: FormKeys.WindowsPercentageArea,
            styles: 'flex flex-col ca-radio-margin ca-radio-width-260',
            defaultErrorMessage: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.ERRORS.REQUIRED',
            validators: [Validators.required],
            options: [
                {
                    key: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.SMALL',
                    value: WindowPercentageArea.Small,
                    subtext: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.SMALL_SUBTEXT'
                },
                {
                    key: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.MEDIUM',
                    value: WindowPercentageArea.Medium,
                    subtext: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.MEDIUM_SUBTEXT'
                },
                {
                    key: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.LARGE',
                    value: WindowPercentageArea.Large,
                    subtext: 'HEATLOAD.WINDOWS_AND_GLAZING.PERCENTAGE_AREA.LARGE_SUBTEXT'
                }
            ],
            value: override?.value
        });
    }

    private getGlazingType(override: RadioQuestionOverride): RadioQuestion {
        const windowTypes: WindowType[] = override.options;
        const questionOptions = windowTypes
            .map(windowType => {
                return {
                    value: windowType,
                    key: windowTypeToPhrase[windowType],
                    order: windowTypeToOrder[windowType]
                };
            })
            .sort((a, b) => a.order - b.order);

        return new RadioQuestion({
            question: 'HEATLOAD.WINDOWS_AND_GLAZING.GLAZING_TYPE.TITLE',
            description: 'HEATLOAD.WINDOWS_AND_GLAZING.GLAZING_TYPE.DESCRIPTION',
            key: FormKeys.TypeOfGlazing,
            styles: 'flex flex-col ca-radio-margin ca-radio-width-400',
            defaultErrorMessage: 'HEATLOAD.WINDOWS_AND_GLAZING.GLAZING_TYPE.ERRORS.REQUIRED',
            validators: [Validators.required],
            options: questionOptions,
            value: override.value
        });
    }
}
